import * as React from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Text } from "@ps-design-system/components/Text";
import ChevronLeftIcon from "@ps-design-system/icons/ChevronLeft/ChevronLeft20";

import { Listing } from "../../../../../types/listings.types";
import { Space } from "../../../../../types/spaces.types";

type ListingEditorLayoutProps = {
  listing: Listing;
  space: Space;
  pageHeader: string;
  testIdPrefix: string;
  /** The divider under the page header; on by default. */
  showHeaderDivider?: boolean;
  pageCaption?: string;
  children: React.ReactNode;
};

/**
 * Shared two-column shell for the mylistings/:listingId section pages
 * (listing details, rules, ...): a sidebar with a back button and the
 * space's name/address, and a main column with a page header, divider, and
 * whatever form the section renders.
 */
const ListingEditorLayout = ({
  listing,
  space,
  pageHeader,
  testIdPrefix,
  showHeaderDivider = true,
  pageCaption,
  children,
}: ListingEditorLayoutProps) => {
  return (
    <div
      className="listing-editor-page tw-grid tw-grid-cols-6"
      data-testid={`${testIdPrefix}-content`}
    >
      <div className="tw-col-span-6 md:tw-col-span-2 tw-flex tw-flex-col md:tw-border-r tw-border-r-gray-200 tw-pt-64 md:tw-pr-20 tw-relative md:tw-pb-64 tw-min-h-[calc(100dvh-72px)]">
        <div className="tw-absolute tw-top-16 tw-left-0">
          <Button
            href={`/mylistings/${listing._id}`}
            variant="text-only"
            prefix={<ChevronLeftIcon />}
          >
            <FormattedMessage
              id="hosting.listing_editor.back_button"
              defaultMessage="Back"
            />
          </Button>
        </div>

        <Text typeface="headline-4-b">{space.title}</Text>
        <Text className="tw-mt-8">{space.street}</Text>
      </div>
      <div className="tw-col-span-6 md:tw-col-span-4 tw-flex tw-flex-col md:tw-pt-64 md:tw-pl-20 tw-pb-64 tw-min-h-[calc(100dvh-72px)]">
        <Text
          typeface="headline-3-b"
          element="h1"
          testId={`${testIdPrefix}-header`}
          className="tw-m-0 tw-hidden md:tw-block"
        >
          {pageHeader}
        </Text>
        {pageCaption && (
          <Text typeface="body-2" element="p" className="tw-m-0">
            {pageCaption}
          </Text>
        )}
        {showHeaderDivider && <Divider spacing={24} />}

        {children}
      </div>
    </div>
  );
};

export { ListingEditorLayout, type ListingEditorLayoutProps };
